import React, { useEffect, useRef, useState } from 'react';
import { X, Star } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────
// Rename modal for the Restorer NPC.
// Opened from the hire sheet's pencil button. Asks for a new nickname
// and charges 1 Telegram Star to apply it. The actual invoice / confirm
// flow lives in the parent (GalleryPage); this component just collects
// the desired name and reports a "submit" intent.
// ──────────────────────────────────────────────────────────────────────────

const PRICE_STARS = 1;
const MAX_NAME = 16;

const sanitize = (raw) => String(raw || '').replace(/[^\p{L}\p{N}\s_.\-']/gu, '').slice(0, MAX_NAME);

const RestorerRenameSheet = ({
  open,
  language = 'en',
  currentName = 'Jeff',
  // Optional subject override so the same sheet works for Molly (the
  // PR Manager) too. When provided, the role label, default name and
  // headings switch. Falls back to "Restorer" / "Jeff" for back-compat.
  subject = 'restorer', // 'restorer' | 'pr_manager'
  onConfirm,
  onClose,
}) => {
  const [shouldRender, setShouldRender] = useState(open);
  const defaultName = subject === 'pr_manager' ? 'Molly' : 'Jeff';
  const [draft, setDraft] = useState(currentName || defaultName);
  const [submitting, setSubmitting] = useState(false);
  const [kbOffset, setKbOffset] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setShouldRender(true); setDraft(currentName || defaultName); return; }
    if (!shouldRender) return;
    const id = setTimeout(() => setShouldRender(false), 220);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!shouldRender) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [shouldRender]);

  // Auto-focus the input when the sheet appears so the keyboard pops
  // up immediately on mobile and the player can start typing.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 240);
    return () => clearTimeout(id);
  }, [open]);

  // Lift the sheet above the on-screen keyboard so the input stays in
  // view (Telegram WebApp / iOS Safari shrink visualViewport rather
  // than the layout viewport, otherwise the bottom-anchored sheet ends
  // up hidden behind the keyboard).
  useEffect(() => {
    if (!shouldRender) return;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const sync = () => {
      const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbOffset(offset);
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      setKbOffset(0);
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const exiting = !open;
  const cleaned = sanitize(draft).trim();
  const valid = cleaned.length >= 1 && cleaned !== (currentName || '').trim();

  const close = () => {
    if (submitting) return;
    onClose && onClose();
  };

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(cleaned);
      close();
    } finally {
      setSubmitting(false);
    }
  };

  const isPr = subject === 'pr_manager';
  const t = language === 'ru'
    ? {
        title: isPr ? 'Сменить имя' : 'Сменить имя',
        subtitle: isPr ? 'Агент · команда' : 'Реставратор · команда',
        label: 'Новое имя',
        helper: `До ${MAX_NAME} символов`,
        sameName: 'Введите другое имя',
        cancel: 'Отмена',
        cta: 'Сменить за',
        priceSuffix: '',
      }
    : {
        title: isPr ? 'Rename Agent' : 'Rename Restorer',
        subtitle: isPr ? 'Agent · team' : 'Restorer · team',
        label: 'New name',
        helper: `Up to ${MAX_NAME} characters`,
        sameName: 'Pick a different name',
        cancel: 'Cancel',
        cta: 'Rename for',
        priceSuffix: '',
      };

  return (
    <div
      data-testid="restorer-rename-sheet"
      className={`fixed inset-0 z-[70] flex items-end sm:items-center justify-center ${
        exiting ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'
      }`}
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-[3px]" onClick={close} />
      <div
        className={`relative w-full sm:max-w-sm bg-[#0E0E18] border border-[#D4AF37]/30 rounded-t-2xl sm:rounded-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.65)] px-5 pt-4 pb-5 ${
          exiting ? 'animate-out slide-out-to-bottom duration-200' : 'animate-in slide-in-from-bottom duration-300'
        }`}
        style={{
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          transform: !exiting && kbOffset > 0 ? `translateY(-${kbOffset}px)` : undefined,
          transition: 'transform 180ms ease-out',
        }}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-[#3F3F46]/70 mb-3 sm:hidden" />

        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="font-heading text-[18px] text-white leading-tight">{t.title}</h2>
            <div className="text-[10px] text-[#A1A1AA]/70 uppercase tracking-[0.18em] mt-1">
              {t.subtitle}
            </div>
          </div>
          <button
            data-testid="rename-close"
            onClick={close}
            className="p-1 rounded-full hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4 text-[#A1A1AA]" />
          </button>
        </div>

        {/* Input */}
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[#A1A1AA]/70">
            {t.label}
          </span>
          <input
            ref={inputRef}
            data-testid="rename-input"
            value={draft}
            onChange={(e) => setDraft(sanitize(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valid) submit();
              if (e.key === 'Escape') close();
            }}
            maxLength={MAX_NAME}
            placeholder={defaultName}
            className="mt-1.5 w-full bg-[#141420] border border-[#D4AF37]/40 rounded-xl px-3 py-2.5 text-[16px] font-semibold text-white outline-none focus:border-[#D4AF37] transition-colors"
          />
          <span className="block text-[10px] text-[#A1A1AA]/55 mt-1">
            {draft.trim() && cleaned === (currentName || '').trim() ? t.sameName : t.helper}
          </span>
        </label>

        {/* Action buttons */}
        <div className="mt-5 flex items-center gap-2">
          <button
            data-testid="rename-cancel"
            onClick={close}
            disabled={submitting}
            className="flex-1 px-3 py-3 rounded-xl border border-[#3F3F46]/60 text-[#E5E5E5] text-[14px] font-medium hover:bg-white/5 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {t.cancel}
          </button>
          <button
            data-testid="rename-confirm"
            onClick={submit}
            disabled={!valid || submitting}
            className={`flex-[1.4] px-3 py-3 rounded-xl border text-[14px] font-bold transition-all flex items-center justify-center gap-1.5 ${
              valid && !submitting
                ? 'border-[#D4AF37]/70 bg-gradient-to-r from-[#D4AF37]/15 to-[#D4AF37]/5 text-[#F5E0A0] shadow-[0_0_18px_rgba(212,175,55,0.25)] hover:from-[#D4AF37]/25 hover:to-[#D4AF37]/10 active:scale-[0.98]'
                : 'border-[#3F3F46]/60 bg-[#141420] text-[#A1A1AA]/70 cursor-not-allowed'
            }`}
          >
            <span>{t.cta}</span>
            <Star className="w-4 h-4" style={{ fill: '#D4AF37', color: '#D4AF37' }} />
            <span className="font-mono tabular-nums" style={{ color: '#D4AF37' }}>{PRICE_STARS}</span>
          </button>
        </div>

        <p className="mt-3 text-[10px] text-[#A1A1AA]/45 text-center leading-snug">
          {language === 'ru'
            ? 'Имя сохранится навсегда. Изменить можно в любое время за 1 ⭐.'
            : 'Name persists forever. You can change it again any time for 1 ⭐.'}
        </p>
      </div>
    </div>
  );
};

export default RestorerRenameSheet;
